<template>
  <b-modal :title="$t('labels.provider_request_log')"
           dialog-class="modal-xxl"
           :modelValue="providerId !== null"
           footer-class="d-flex justify-content-end"
           @update:modelValue="$event === false && (providerId = null)">
    <template #default>
      <LoaderWrapper ref="loader" class="h-100">
        <div class="p-3 row pt-0">
          <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-3">
            <div class="col p-0">
              <div class="">
                <input type="text" class="form-control form-control-sm" :placeholder="helper.trans('labels.id')" v-model="filters.invoiceId">
              </div>
            </div>
          </div>

          <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
            <div class="col p-0">
              <div class="">
                <DatePicker v-model="filters.fromDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.from_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
            <div class="col p-0">
              <div class="">
                <DatePicker v-model="filters.toDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.to_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div class="form-group mb-2 col">
            <div class="col-auto p-0 text-end">
              <button type="button" @click.prevent="() => applyFilters()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">
                {{ helper.trans('button.apply') }}
              </button>
            </div>
          </div>

        </div>


        <div class="p-3 pt-0" v-if="providerId">
          <div class="overflow-auto">
            <table class="table table-striped border-light align-middle text-left position-relative ">
              <thead>
              <tr>
                <th class="text-capitalize-first">{{ $t('labels.id_date') }}</th>
                <th class="text-capitalize-first">{{ $t('labels.request') }}</th>
                <th class="text-capitalize-first">{{ $t('labels.response') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in paginatedResponse.items" :key="index" class="">
                <td>
                  <div class="d-flex flex-column mt-1 mb-1">
                    <span>
                      <span v-b-tooltip.hover :title="item.invoiceId">{{ item.invoiceId.substring(0, 8) }}...</span> <copy-button :value="item.invoiceId"></copy-button>
                    </span>
                  </div>
                  <div>
                    <small class="text-secondary">{{ dateHelper.format(item.requestAt, 'YYYY-MM-DD') }}</small>
                  </div>
                  <div>
                    <small class="text-secondary">{{ dateHelper.format(item.requestAt, 'HH:mm:ss.SSS') }}</small>
                  </div>
                  <div>
                    <small class="text-secondary">{{ dateHelper.format(item.responseAt, 'HH:mm:ss.SSS') }}</small>
                  </div>
                  <div><small class="text-secondary">{{ numberFormatter.formatNumber(item.duration, 3) }}s</small></div>
                </td>
                <td class="align-top" style="max-width: 500px">
                  <pre><code style="    white-space: pre-line;">
                      {{item.method}} {{item.uri}}

                      BODY
                      {{item.body}}

                      HEADERS
                      {{JSON.stringify(item.headers)}}
                    </code></pre>
                </td>
                <td class="align-top" style="max-width: 500px">
                  <pre><code style="white-space: pre-line;">
                    CODE: {{item.responseCode}}

                    BODY
                    {{ item.response }}
                    </code></pre>
                </td>
              </tr>
              </tbody>
            </table>
            <b-pagination
              v-if="paginatedResponse.totalCount > paginatedResponse.items.length"
              v-model="page"
              :total-rows="paginatedResponse.totalCount"
              :per-page="paginatedResponse.limit"
              :limit="20"
              @update:modelValue="load()"
              :prev-text="helper.trans('button.back')"
              :next-text="helper.trans('button.next')"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="providerId = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.ok')  }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {defineComponent, PropType, Prop, watch, ref} from 'vue'
import {$t, helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import {api} from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {BModal, BPagination} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {DatePicker} from "v-calendar";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import ProviderRequestDTO from "@/models/ProviderRequestDTO";
import CopyButton from "@/components/CopyButton.vue";

export default defineComponent({
  methods: {$t},
  components: {
    CopyButton,
    BPagination,
    DatePicker,
    LoaderWrapper,
    BModal,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const defaultFilters = {invoiceId: null, fromDate: null, toDate:null} as any;

    const state = reactive({
      filters: {...defaultFilters} as any,
      providerId: null as number|null,
      page: 1,
      limit: 20,
      paginatedResponse: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<ProviderRequestDTO>
    });

    const show = (providerId: number) => {
      state.filters = {...defaultFilters}
      state.providerId = providerId
      applyFilters()
    }

    const applyFilters = async () => {
      state.paginatedResponse.items = []
      state.page = 1;
      load()
    }

    const load = async () => {
      if (!state.providerId) {
        return;
      }

      loader.value?.show()
      try {
        const offset = state.limit * (state.page - 1)
        state.paginatedResponse = await api.call.providers.requests(state.providerId, {
          limit: state.limit,
          offset: offset
        }, {
          invoiceId: state.filters.invoiceId,
          fromDate: dateHelper.toUTC(state.filters.fromDate),
          toDate: dateHelper.toUTC(state.filters.toDate),
        });
      } finally {
        loader.value?.hide()
      }
    }

    return {
      ...toRefs(state),
      show,
      applyFilters,
      load,
      loader,
      dateHelper,
      numberFormatter,
      helper
    }
  }
})
</script>
